import type { BuildPreviewResult } from "@seedance/workflow";
import type { CanvasFlowState } from "./xyflow-adapter.js";

export type CanvasRenderProjection = Pick<CanvasFlowState, "nodes" | "edges">;

function reuse<T extends object>(next: T, previous: T | undefined): T {
  if (next === previous || !previous) return next;
  const keys = Object.keys(next) as Array<keyof T>;
  return keys.length === Object.keys(previous).length && keys.every(key =>
    Object.hasOwn(previous, key) && Object.is(next[key], previous[key])) ? previous : next;
}

function reuseRecords<T extends object>(next: T[], previous: T[]): T[] {
  return reuse(next.map((item, index) => reuse(item, previous[index])), previous);
}

function reusePreview(next: BuildPreviewResult, previous: BuildPreviewResult): BuildPreviewResult {
  return reuse({ ...next,
    parameters: reuse(next.parameters, previous.parameters),
    references: reuseRecords(next.references, previous.references),
    errors: reuseRecords(next.errors, previous.errors),
    warnings: reuseRecords(next.warnings, previous.warnings)
  }, previous);
}

/** Pure identity optimization AFTER deriving current data and validating previews.
 * Compare every own field. Never traverse/reuse a different editor document, asset,
 * callback or canvas node: those identities carry current editing/authorization state.
 * No shared cache, mutation, graph validation or request authorization occurs here. */
export function shareCanvasRenderProjection(next: CanvasRenderProjection, previous?: CanvasRenderProjection): CanvasRenderProjection {
  if (!previous || next === previous) return next;
  const previousNodes = new Map(previous.nodes.map(node => [node.id, node]));
  const previousEdges = new Map(previous.edges.map(edge => [edge.id, edge]));
  const nodes = next.nodes.map(node => {
    const before = previousNodes.get(node.id);
    if (!before || before === node) return node;
    let data = node.data;
    if (data.canvasNode.kind === "seedance" && before.data.canvasNode.kind === "seedance") {
      data = { ...data };
      if (data.preview && before.data.preview) data.preview = reusePreview(data.preview as BuildPreviewResult, before.data.preview as BuildPreviewResult);
      if (data.referenceCounts && before.data.referenceCounts) data.referenceCounts = reuse(data.referenceCounts as object, before.data.referenceCounts as object);
    }
    data = reuse(data, before.data);
    return reuse({ ...node, data }, before);
  });
  const edges = next.edges.map(edge => {
    const before = previousEdges.get(edge.id);
    if (!before || before === edge) return edge;
    const candidate = { ...edge };
    if (candidate.markerEnd && typeof candidate.markerEnd === "object" && before.markerEnd && typeof before.markerEnd === "object") {
      candidate.markerEnd = reuse(candidate.markerEnd, before.markerEnd);
    }
    if (candidate.data && before.data) candidate.data = reuse(candidate.data, before.data);
    return reuse(candidate, before);
  });
  return reuse({ nodes: reuse(nodes, previous.nodes), edges: reuse(edges, previous.edges) }, previous);
}
